import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';

export interface ObservedElements {
    [key: string]: { isInView: boolean };
}

interface InViewOptions extends IntersectionObserverInit {
    once?: boolean;
}

export function useInView(ref: MutableRefObject<HTMLElement | HTMLElement[] | null>, options?: InViewOptions) {
    const [elements, setElements] = useState<ObservedElements>({});
    const [isInView, setIsInView] = useState(false);
    const isInViewRef = useRef(isInView);
    const isArray = Array.isArray(ref.current);

    const observerCallback = useCallback(
        (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
            entries.forEach(entry => {
                const isInViewCurrently = entry.isIntersecting;
                const key = entry.target.getAttribute('data-observer-key');

                if (isArray) {
                    if (!key)
                        throw new Error(
                            'Encountered entry with no key. You should add a unique data-observer-key to every element passed to the isInView hook.'
                        );

                    setElements(prev => ({ ...prev, [key]: { isInView: isInViewCurrently } }));
                } else {
                    setIsInView(isInViewCurrently);
                }

                if (isInViewRef.current && options?.once) {
                    observer.unobserve(entry.target);
                }
            });
        },
        [isArray, options?.once]
    );

    useEffect(() => {
        isInViewRef.current = isInView;
    }, [isInView]);

    useEffect(() => {
        const observer = new IntersectionObserver(observerCallback, options);
        const observableElements = ([] as (HTMLElement | null)[]).concat(ref.current ?? []);
        observableElements.forEach(el => el && observer.observe(el));

        return () => {
            observer?.disconnect();
        };
    }, []);

    return isArray ? elements : isInView;
}
